// src/lib/api-pipelines.ts
//
// API client pra Pipeline — alinhado β2-A + β2-D.
//
// Endpoints REST canonical:
//   - GET    /v1/pipelines                              → lista
//   - GET    /v1/pipelines/:idOrSlug                    → detalhe
//   - POST   /v1/pipelines                              → cria (β2-D, NOVO)
//   - POST   /v1/pipelines/:id/start                    → instancia run (β2-D, NOVO)
//   - POST   /v1/pipelines/:id/instantiate              → clone global → tenant
//   - POST   /v1/pipelines/:id/promote                  → tenant → global
//
// Wizard conversacional (mantido — usado por PipelineConversationalWizardPage):
//   - POST /v1/wizards/create-pipeline/start          → wizard_execution_id
//   - POST /v1/wizards/executions/:id/turn            → genérico state-backed
//   - POST /v1/wizards/executions/:id/publish-pipeline → publica via state.collected
//
// O modo manual (this page) AGORA chama `createPipeline` direto — não passa mais
// por @pipeline-builder. β2-A removeu o `mode='manual'` do builder; @pipeline-runner
// recebe `definition.create` direto via REST.
//
// Schema canonical v2: SEM outputs[] top-level no pipeline (admin cravou). Pipeline
// só tem inputs e steps; outputs vivem em cada step (consumíveis por steps seguintes).
// `summary` e step.description são opcionais — LLM gera no publish quando ausentes.

/**
 * F-FINAL-83 (2026-05-27) — Base URL configuravel via env var (espelha api.ts).
 * Em prod aponta direto pro Tailscale funnel bypassando rewrite do Vercel
 * (timeout ~30-60s). Fallback `/api` preserva dev local + compat se env var
 * faltar. Ver src/lib/api.ts pra contexto completo.
 */
import type { PromoteResponse } from './api';

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '/api';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: { code: string; message: string };
}

// Mesma chave que api.ts usa pra auth Bearer (api.ts:1062 usa 'raios_token').
// Sem este header, backend retorna "Token ausente" e listPipelines falha.
const TOKEN_STORAGE_KEY = 'raios_token';

async function fetchJson<T>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((init?.headers as Record<string, string>) ?? {}),
  };
  if (typeof window !== 'undefined') {
    const token = window.localStorage.getItem(TOKEN_STORAGE_KEY);
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers,
    credentials: 'include',
  });
  const json = (await res.json()) as ApiResponse<T> | T;
  if (!res.ok) {
    const errMsg = (json as ApiResponse<T>).error?.message ?? `HTTP ${res.status}`;
    throw new Error(errMsg);
  }
  if (
    typeof json === 'object'
    && json !== null
    && 'success' in json
    && (json as ApiResponse<T>).success === false
  ) {
    throw new Error((json as ApiResponse<T>).error?.message ?? 'API error');
  }
  return ((json as ApiResponse<T>).data ?? (json as T));
}

// ─── IO types canonical (espelha src/shared/types/io-schema.ts do backend) ─

export const IO_INPUT_TYPES = [
  'string',
  'number',
  'boolean',
  'object',
  'array',
  'enum',
  'longtext',
  'date',
] as const;

export const IO_OUTPUT_TYPES = [
  'string',
  'json',
  'object',
  'image',
  'video',
  'audio',
  'document',
] as const;

export type IOInputType = (typeof IO_INPUT_TYPES)[number];
export type IOOutputType = (typeof IO_OUTPUT_TYPES)[number];

// ─── Tipos de leitura (lista/detalhe/runs) ────────────────────────────────

export interface PipelineSummary {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  tenant_id: string | null;
  scope_label: 'global' | 'tenant';
  is_active: boolean;
  requires_business: boolean;
  parent_global_id: string | null;
  is_promotable: boolean;
  latest_version_id: string | null;
  latest_version_label: string | null;
  created_at: string;
}

export interface PipelineVersionSummary {
  id: string;
  pipeline_id: string;
  version: string;
  changelog: string | null;
  config: Record<string, unknown>;
  published_at: string;
  deprecated_at: string | null;
}

export interface PipelineRunSummary {
  id: string;
  pipeline_id: string;
  pipeline_version_id: string | null;
  demand_id: string;
  business_id: string;
  status:
    | 'pending'
    | 'decomposing'
    | 'executing'
    | 'completed'
    | 'failed'
    | 'blocked'
    | 'cancelled';
  tasks_planned: number;
  tasks_created: number;
  tasks_done: number;
  tasks_failed: number;
  scope_growth_rate: number | null;
  success_rate: number | null;
  failure_rate: number | null;
  config: Record<string, unknown>;
  started_at: string;
  completed_at: string | null;
  // P1.12 Frente F — colunas canônicas v3.0.0
  // backward_depth_current: quantos backwards já consumidos (R6: comparado
  // contra pipeline.config.max_backward_depth pra disparar débito).
  // estimated_duration_seconds: ETA canônico do run (R12).
  backward_depth_current?: number | null;
  estimated_duration_seconds?: number | null;
  /** Correção UX v2 C3 — step corrente REAL derivado server-side (semântica do board). */
  steps_total?: number | null;
  current_step_number?: number | null;
  current_step_slug?: string | null;
  /** DEM-2051 — nome LEGÍVEL do pipeline da run (pipelines.name), derivado
   *  server-side. Segunda linha do card exibe-o em vez do slug cru. */
  pipeline_name?: string | null;
}

// ─── Schema canonical v2 (espelha PR5 CollectedFieldsSchema do backend) ───

export interface PipelineInputDef {
  /** snake_case (validado por NAME_SNAKE_REGEX no backend). */
  arg: string;
  type: IOInputType;
  required: boolean;
  description: string;
  /** Obrigatório quando type='enum' (>= 1 valor). */
  enum_values?: string[];
}

export interface PipelineStepExecutor {
  /** OBRIGATÓRIO — agente dono do step. */
  agent_slug: string;
  /** OPCIONAL — skill que o agente invoca. */
  skill_slug?: string;
}

export interface PipelineStepDef {
  step_number: number;
  /** kebab-case, auto-derivado do name. */
  slug: string;
  name: string;
  description?: string;
  executor: PipelineStepExecutor;
  /** Step slugs anteriores; '[]' quando primeiro. */
  depends_on: string[];
  /** Refs: 'pipeline.inputs.X' ou 'step_N.outputs.Y'. */
  inputs: string[];
  /** snake_case. */
  outputs: string[];
  /** Opt-in fanout: 'step_N.outputs.X' itera fanout. */
  iterates_over?: string;
  instructions: string[];
  /** Default true. */
  mandatory: boolean;
  /** Default 3600. */
  timeout_seconds: number;
}

/**
 * Payload canonical de publish do pipeline (wizard conversacional).
 *
 * v2 final (β2-A): SEM `outputs[]` top-level. Pipeline tem só inputs e steps.
 * Step.outputs[] são consumíveis por steps subsequentes via refs.
 *
 * REMOVIDO:
 *   - outputs[] top-level (admin cravou)
 *   - kind/target_slug/handler_name em step
 *   - decomposer_agent_slug / decomposer_fallback_agents / decomposer_timeout_seconds
 *   - fallback_strategy complexa por step
 */
export interface PipelinePublishPayload {
  // Wave 1 — identidade (frontmatter)
  slug: string;
  name: string;
  description?: string;
  scope: 'global' | 'tenant';
  requires_business: boolean;

  // Wave 2 — inputs do pipeline (top-level) — pode ser vazio
  inputs: PipelineInputDef[];

  // Wave 3 — steps
  steps: PipelineStepDef[];

  // Wave 4 — publish metadata
  /** semver (ex: '1.0.0'). */
  initial_version: string;
  changelog?: string;
  /**
   * Opcional — backend @pipeline-builder gera (LLM-driven) no publish quando
   * ausente. 1-500 chars.
   */
  summary?: string;
}

/**
 * Payload do endpoint REST canonical β2-D — POST /v1/pipelines.
 *
 * Diferenças vs PipelinePublishPayload (wizard conversacional):
 *   - `config` é jsonb canonical v2 já montado (frontmatter, inputs, steps, global_config)
 *   - `version` substitui `initial_version`
 *   - Não passa por @pipeline-builder LLM extraction — vai direto pro @pipeline-runner
 *     action `definition.create`.
 */
export interface CreatePipelinePayload {
  slug: string;
  name: string;
  description?: string;
  scope?: 'global' | 'tenant';
  requires_business?: boolean;
  /** Canonical v2 jsonb: { frontmatter, inputs[], steps[], global_config? }. */
  config: Record<string, unknown>;
  version?: string;
  changelog?: string;
  is_active?: boolean;
}

export interface CreatePipelineResponse {
  pipeline: { id: string; slug: string; name: string };
  pipeline_version_id?: string;
  version?: string;
}

export interface StartPipelineResponse {
  pipeline_run_id: string;
}

/**
 * Payload do endpoint POST /v1/pipelines/:id/versions (PIPE-02/03).
 *
 * `config` é o canonical v2 jsonb COMPLETO (frontmatter, inputs[], steps[],
 * global_config?) — versões são imutáveis, então o caller envia o config
 * inteiro da nova versão (tipicamente: config da versão ativa + edição).
 */
export interface CreatePipelineVersionPayload {
  config: Record<string, unknown>;
  /** semver `^\d+\.\d+\.\d+$` — ausente = backend bumpa minor da última. */
  version?: string;
  /** Max 4000 chars. */
  changelog?: string;
}

export interface CreatePipelineVersionResponse {
  pipeline_id: string;
  pipeline_slug: string;
  version_id: string;
  version: string;
  changelog: string | null;
  published_at: string;
  /** Backend já move o ponteiro — a nova versão vira a latest/ativa. */
  latest_version_id: string;
}

// ─── Responses ────────────────────────────────────────────────────────────

export interface PipelinePublishResponse {
  pipeline_id: string;
  pipeline_version_id: string;
  pipeline_slug: string;
  version: string;
}

/**
 * Shape canonical retornado por `POST /v1/wizards/:slug/start`
 * (`src/wizards/service.ts:123` no backend).
 *
 * O backend retorna `{ execution: WizardExecutionRow, demand_id, opening_message }`.
 * Esta interface normaliza pra contrato cliente — `startPipelineWizard()` extrai
 * `execution.id` pra `wizard_execution_id` e `execution.state.current_wave` (se
 * presente, default 1).
 */
export interface PipelineWizardStartResponse {
  wizard_execution_id: string;
  /** Mensagem template seedada em wizards.opening_message_template. */
  opening_message: string | null;
  current_wave: 1 | 2 | 3 | 4;
  /** ID de demand linkada (null pra create-pipeline — F-FINAL-26 demand é exclusivo de pipeline-origin). */
  demand_id: string | null;
}

interface RawWizardStartResponse {
  execution: {
    id: string;
    tenant_id: string;
    state: Record<string, unknown> | null;
    status: string;
  };
  demand_id: string | null;
  opening_message: string | null;
}

export interface PipelineWizardTurnRequest {
  wizard_execution_id: string;
  user_message: string;
}

export interface PipelineWizardTurnResponse {
  assistant_message: string;
  current_wave: 1 | 2 | 3 | 4;
  /** Estado acumulado do wizard (Partial enquanto coletando). */
  collected: Partial<PipelinePublishPayload>;
  is_complete: boolean;
}

// ─── LISTA / DETALHE ──────────────────────────────────────────────────────

export async function listPipelines(): Promise<PipelineSummary[]> {
  return fetchJson<PipelineSummary[]>('/v1/pipelines');
}

export async function getPipeline(
  idOrSlug: string,
): Promise<PipelineSummary & { versions: PipelineVersionSummary[] }> {
  return fetchJson(`/v1/pipelines/${encodeURIComponent(idOrSlug)}`);
}

/**
 * Reforma UX §7.6 — switch ativar/desativar pipeline (tenant-only; global=403).
 * Backend: PATCH /v1/pipelines/:id { is_active }.
 */
export async function patchPipeline(
  id: string,
  payload: { is_active: boolean },
): Promise<PipelineSummary> {
  return fetchJson<PipelineSummary>(
    `/v1/pipelines/${encodeURIComponent(id)}`,
    { method: 'PATCH', body: JSON.stringify(payload) },
  );
}

// ─── LIFECYCLE (path params canonical) ────────────────────────────────────

/**
 * Clona pipeline global → tenant (cópia inicial pra customização).
 *
 * PR7: corrige URL — antes batia em `/v1/pipelines/instantiate-global` (404).
 * Backend (src/api/routes/pipeline.routes.ts:163) expõe `:id/instantiate`.
 */
export async function instantiateGlobalPipeline(
  globalPipelineId: string,
): Promise<{ pipeline_id: string }> {
  return fetchJson(
    `/v1/pipelines/${encodeURIComponent(globalPipelineId)}/instantiate`,
    { method: 'POST' },
  );
}

/**
 * Promove pipeline tenant → global (admin platform).
 *
 * PR7: corrige URL — antes batia em `/v1/pipelines/promote-to-global` (404).
 * Backend (src/api/routes/pipeline.routes.ts:179) expõe `:id/promote`.
 *
 * F8 (global-promotion): a rota agora dispara a tool `promote_asset`, que
 * promove o pipeline E em cascata TODAS as skills e agentes referenciados,
 * sincronizando o storage. Retorna o resultado da tool (cascade/missing_deps/
 * synced/reused/message). `pipeline_id` continua presente pra compat.
 */
export async function promoteTenantPipeline(
  tenantPipelineId: string,
): Promise<PromoteResponse & { pipeline_id?: string }> {
  return fetchJson(
    `/v1/pipelines/${encodeURIComponent(tenantPipelineId)}/promote`,
    { method: 'POST' },
  );
}

// ─── WIZARD STATE-BACKED (3-step canonical) ──────────────────────────────

/**
 * Step 1 — start wizard execution (genérico). Cria row em wizard_executions
 * com wizards.slug='create-pipeline' e state vazio. Retorna o ID pro caller
 * conduzir os turns e publish.
 *
 * Backend: POST /v1/wizards/:slug/start (src/wizards/routes.ts:133).
 */
export async function startPipelineWizard(
  mode: 'manual' | 'conversational' = 'conversational',
): Promise<PipelineWizardStartResponse> {
  // PR12-fix: backend retorna {execution, demand_id, opening_message}.
  // Smoke E2E (Amos PR12) descobriu divergência de shape — frontend antes
  // esperava `wizard_execution_id` flat. Normaliza aqui pra contrato cliente.
  const raw = await fetchJson<RawWizardStartResponse>(
    '/v1/wizards/create-pipeline/start',
    {
      method: 'POST',
      body: JSON.stringify({ mode }),
    },
  );
  const state = (raw.execution.state ?? {}) as Record<string, unknown>;
  const rawWave = state['current_wave'];
  const currentWave: 1 | 2 | 3 | 4 =
    rawWave === 1 || rawWave === 2 || rawWave === 3 || rawWave === 4 ? rawWave : 1;
  return {
    wizard_execution_id: raw.execution.id,
    opening_message: raw.opening_message,
    current_wave: currentWave,
    demand_id: raw.demand_id,
  };
}

/**
 * Step 2 — turn state-backed (genérico). Dispatcha @wizard-runner →
 * @pipeline-builder em modo conversacional; history/collected/wave vivem em
 * wizard_executions.state (não no body do request).
 *
 * Backend: POST /v1/wizards/executions/:id/turn (src/wizards/routes.ts:160).
 */
export async function pipelineWizardTurn(
  req: PipelineWizardTurnRequest,
): Promise<PipelineWizardTurnResponse> {
  return fetchJson<PipelineWizardTurnResponse>(
    `/v1/wizards/executions/${encodeURIComponent(req.wizard_execution_id)}/turn`,
    {
      method: 'POST',
      body: JSON.stringify({ user_message: req.user_message }),
    },
  );
}

/**
 * Step 3 — publica pipeline a partir de wizard_executions.state.collected.
 *
 * Backend (PR6, src/wizards/routes.ts:1056): valida wizard.slug='create-pipeline',
 * dispatcha @pipeline-builder em mode='manual' com collected do state, marca
 * wizard como complete via @op-runner→wizard-complete.
 */
export async function publishPipelineFromWizard(
  wizardExecutionId: string,
  collected?: PipelinePublishPayload,
): Promise<PipelinePublishResponse> {
  // PR7-fix (pipeline-canonical-rebuild): endpoint publish-pipeline aceita
  // body.collected opcional. Quando presente (modo manual UI), backend usa
  // direto sem ler state.collected. Quando ausente, backend lê do state
  // (modo conversational com turns acumulados).
  //
  // Backend retorna shape com {entity_id, pipeline_version_id, slug, version}
  // — normalizamos pra PipelinePublishResponse aqui no client.
  const raw = await fetchJson<{
    entity_id: string;
    pipeline_version_id: string;
    slug: string;
    version: string;
  }>(
    `/v1/wizards/executions/${encodeURIComponent(wizardExecutionId)}/publish-pipeline`,
    {
      method: 'POST',
      ...(collected ? { body: JSON.stringify({ collected }) } : {}),
    },
  );
  return {
    pipeline_id: raw.entity_id,
    pipeline_version_id: raw.pipeline_version_id,
    pipeline_slug: raw.slug,
    version: raw.version,
  };
}

/**
 * β2-D — POST /v1/pipelines (canonical REST direto).
 *
 * Chamado pela UI MANUAL (PipelineManualWizardPage). Não passa por
 * @pipeline-builder LLM extraction — vai direto pro @pipeline-runner action
 * `definition.create`. O caller monta `config` canonical v2 jsonb com:
 * `{ frontmatter, inputs[], steps[], global_config? }` (SEM outputs[] top-level).
 *
 * Backend: src/api/routes/pipeline.routes.ts handler POST /v1/pipelines (β2-D).
 */
export async function createPipeline(
  payload: CreatePipelinePayload,
): Promise<CreatePipelineResponse> {
  return fetchJson<CreatePipelineResponse>('/v1/pipelines', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/**
 * PIPE-02/03 — POST /v1/pipelines/:id/versions (nova versão IMUTÁVEL).
 *
 * Tenant-only (403 global/outro tenant). 409 versão duplicada; 422 config
 * inválida (CHECKs depends_on/io types). Backend atualiza latest_version_id
 * — a nova versão vira a ativa; as anteriores permanecem imutáveis.
 *
 * ATENÇÃO: rota ativa no backend pós-restart — até lá 404 (a UI degrada com
 * toast claro).
 */
export async function createPipelineVersion(
  id: string,
  payload: CreatePipelineVersionPayload,
): Promise<CreatePipelineVersionResponse> {
  return fetchJson<CreatePipelineVersionResponse>(
    `/v1/pipelines/${encodeURIComponent(id)}/versions`,
    { method: 'POST', body: JSON.stringify(payload) },
  );
}

/**
 * β2-D — POST /v1/pipelines/:id/start (instancia run de uma demand existente).
 *
 * Dispara @pipeline-runner action `run.start` pra demand_id+inputs especificados.
 */
export async function startPipeline(
  pipelineId: string,
  demandId: string,
  inputs?: Record<string, unknown>,
): Promise<StartPipelineResponse> {
  return fetchJson<StartPipelineResponse>(
    `/v1/pipelines/${encodeURIComponent(pipelineId)}/start`,
    {
      method: 'POST',
      body: JSON.stringify({ demand_id: demandId, ...(inputs ? { inputs } : {}) }),
    },
  );
}

// ─── PIPELINE RUNS ────────────────────────────────────────────────────────

export async function listPipelineRuns(
  filters?: { pipeline_id?: string; demand_id?: string; status?: string; light?: boolean },
): Promise<PipelineRunSummary[]> {
  const params = new URLSearchParams();
  if (filters?.pipeline_id) params.set('pipeline_id', filters.pipeline_id);
  if (filters?.demand_id) params.set('demand_id', filters.demand_id);
  // AGT-16: CSV de status (backend faz IN). Backend antigo ignora o param
  // (devolve tudo) — degrade graceful até o restart.
  if (filters?.status) params.set('status', filters.status);
  // AGT-17: light=1 — sem `config` (o grosso do payload); etapa derivada de
  // pipeline_run_steps no servidor. Backend antigo ignora (graceful).
  if (filters?.light) params.set('light', '1');
  const qs = params.toString();
  return fetchJson<PipelineRunSummary[]>(
    `/v1/pipeline-runs${qs ? '?' + qs : ''}`,
  );
}

export async function getPipelineRun(id: string): Promise<PipelineRunSummary> {
  return fetchJson<PipelineRunSummary>(
    `/v1/pipeline-runs/${encodeURIComponent(id)}`,
  );
}
